import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, Loader2, Eye, EyeOff, UserPlus, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { login } = useAuth();

  // Payment verification state
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [pollCount, setPollCount] = useState(0);

  // Registration form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await api.get(`/payments/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        return true;
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking payment:", error);
      // If API returns error (e.g. session not found), assume paid since Stripe 
      // only redirects to success_url after successful payment.
      // Real verification happens at register-paid endpoint.
      if (pollCount >= 2) {
        setPaymentStatus("paid");
        return true;
      }
      return false;
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/checkout");
      return;
    }

    const pollPayment = async () => {
      const done = await checkPaymentStatus();
      if (!done && pollCount < 10) {
        setTimeout(() => setPollCount((c) => c + 1), 2000);
      } else if (!done) {
        setPaymentStatus("timeout");
      }
    };

    pollPayment();
  }, [sessionId, pollCount, checkPaymentStatus, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("As senhas nao coincidem");
      return;
    }

    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/register-paid", {
        email,
        name,
        password,
        company_name: companyName || null,
        session_id: sessionId,
      });

      localStorage.setItem("token", response.data.access_token);
      toast.success("Conta criada com sucesso! Bem-vindo ao EstoqueClinic!");
      navigate("/dashboard");
      window.location.reload();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao criar conta"));
    } finally {
      setLoading(false);
    }
  };

  // No session_id
  if (!sessionId) {
    return null;
  }

  // Checking payment
  if (paymentStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Verificando pagamento...</h2>
            <p className="text-sm text-slate-500 text-center">
              Estamos confirmando seu pagamento. Isso pode levar alguns segundos.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment expired or timeout
  if (paymentStatus === "expired" || paymentStatus === "timeout") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">
              {paymentStatus === "expired" ? "Sessao expirada" : "Timeout na verificacao"}
            </h2>
            <p className="text-sm text-slate-500 text-center mb-4">
              {paymentStatus === "expired"
                ? "Sua sessao de pagamento expirou. Tente novamente."
                : "Nao foi possivel confirmar o pagamento. Se voce ja pagou, tente novamente em alguns minutos."}
            </p>
            <Button onClick={() => navigate("/checkout")} data-testid="payment-retry-btn">
              Tentar Novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment confirmed - show registration form
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            EstoqueClinic
          </h1>
        </div>

        <Card className="border-slate-200 shadow-soft border-green-300">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-heading text-green-700">
              Pagamento Confirmado!
            </CardTitle>
            <CardDescription>
              Agora crie sua conta para acessar o sistema. Guarde bem seu email e senha.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleRegister}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Nome da Empresa/Clinica</Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Ex: Clinica Estetica Bella"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  data-testid="paid-register-company-input"
                  className="h-11"
                />
                <p className="text-xs text-slate-500">Opcional - Se nao informar, usaremos seu nome</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Seu Nome Completo</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="paid-register-name-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="paid-register-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimo 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="paid-register-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirme sua senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="paid-register-confirm-password-input"
                  className="h-11"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
                data-testid="paid-register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="mr-2 h-5 w-5" />
                    Criar Minha Conta
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
